import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  faArrowDownToBracket,
  faCode,
  faFolder,
  faLightbulb,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faSparkles } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { toast } from 'sonner';

import { downloadUnsplashImage } from '@/components/asset-manager/components/unsplash/api';
import { UploadFilesPanel } from '@/components/asset-manager/components/upload-files-content';
import { UrlImportSection } from '@/components/asset-manager/components/url-import-section';
import type { Asset, UnsplashImage } from '@/components/asset-manager/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TextInput } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SmallText } from '@/components/ui/typography';
import type { AssetFileTypes } from '@/lib/assets-config';
import { cn, getDocument } from '@/lib/utils';
import { extractYouTubeVideoId } from '@/lib/youtube';
import { useAssetsStore } from '@/stores/assets-store';
import type { ImageSuggestionTarget, SelectedAssetData } from '@/types/image';
import { SuggestionsContent } from '../asset-manager/components/suggestions-content';
import { ASSISTANT_PANEL_WIDTH_PX } from '../asset-manager/tabs/constants';
import { ImageSelectorCreateImageFeature } from './image-selector-create-image-feature';
import { LibraryFilesContent } from './library-files-content';

function YouTubeTabContent({
  initialUrl,
  onSelect,
  onCancel,
}: {
  initialUrl?: string;
  onSelect: (url: string) => void;
  onCancel: () => void;
}) {
  const [urlInput, setUrlInput] = useState(initialUrl ?? '');
  const [error, setError] = useState<string | null>(null);

  const handleApply = useCallback(() => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    const videoId = extractYouTubeVideoId(trimmed);
    if (!videoId) {
      setError('Please enter a valid YouTube URL');
      return;
    }
    onSelect(trimmed);
  }, [urlInput, onSelect]);

  return (
    <div className={cn('flex flex-col gap-4 pt-4')}>
      <div className="space-y-2">
        <SmallText className="font-semibold text-foreground">YouTube URL</SmallText>
        <TextInput
          value={urlInput}
          onChange={(e) => {
            setUrlInput(e.target.value);
            setError(null);
          }}
          placeholder="http://youtube.com/"
          invalid={!!error}
          errorText={error || undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && urlInput.trim()) {
              handleApply();
            }
          }}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleApply} disabled={!urlInput.trim()}>
          Apply
        </Button>
      </div>
    </div>
  );
}

interface ImageSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageData: SelectedAssetData) => void;
  onYouTubeSelect?: (youtubeUrl: string) => void;
  fileTypes?: AssetFileTypes;
  selectedAssetId?: string | null;
  selectedFolderId?: string | null;
  initialImageUrl?: string | null;
  initialYouTubeUrl?: string | null;
  selectLabel?: string;
  title?: string;
  sectionId?: string;
  imageSuggestionTarget?: ImageSuggestionTarget;
  enableCreateImage?: boolean;
}

type ModalTab = 'suggestions' | 'create' | 'library' | 'upload' | 'youtube';

export function ImageSelectorModal(props: ImageSelectorModalProps) {
  if (!props.isOpen) return null;
  return <ImageSelectorModalContent {...props} />;
}

function ImageSelectorModalContent({
  isOpen,
  onClose,
  onSelectImage,
  onYouTubeSelect,
  selectedAssetId: initialAssetId,
  selectedFolderId,
  initialImageUrl,
  initialYouTubeUrl,
  fileTypes = 'images',
  selectLabel = 'Select',
  title,
  sectionId,
  imageSuggestionTarget,
  enableCreateImage = false,
}: ImageSelectorModalProps) {
  const { assets, assetsByFolder, singleAssets, loadAssets, markStale } = useAssetsStore();
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [pinnedAssetId, setPinnedAssetId] = useState<string | null>(null);
  const isImageMode = fileTypes === 'images';
  const createImageEnabled = isImageMode && enableCreateImage;
  const showYouTubeTab = fileTypes === 'videos' && Boolean(onYouTubeSelect);

  // Existing image: open the Asset Library, where it is pinned and highlighted.
  // Otherwise lead with fresh stock suggestions.
  const [activeTab, setActiveTab] = useState<ModalTab>(() => {
    if (initialYouTubeUrl && showYouTubeTab) return 'youtube';
    return isImageMode && !initialAssetId && !initialImageUrl ? 'suggestions' : 'library';
  });
  const [hasVisitedSuggestions, setHasVisitedSuggestions] = useState(false);
  const [isCreateFeatureBusy, setIsCreateFeatureBusy] = useState(false);
  const [selectedSuggestionImage, setSelectedSuggestionImage] = useState<UnsplashImage | null>(
    null,
  );
  const [isSelectingSuggestion, setIsSelectingSuggestion] = useState(false);
  const isSelectingSuggestionRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setPinnedAssetId(initialAssetId ?? null);
    setSelectedAssetId(initialAssetId ?? null);
    setSelectedSuggestionImage(null);
    markStale();
    void loadAssets(fileTypes);
  }, [isOpen, fileTypes, initialAssetId, loadAssets, markStale]);

  useEffect(() => {
    if (
      (activeTab === 'create' && !createImageEnabled) ||
      (activeTab === 'suggestions' && !isImageMode)
    ) {
      setActiveTab('library');
    }
  }, [activeTab, createImageEnabled, isImageMode]);

  useEffect(() => {
    if (activeTab === 'suggestions') {
      setHasVisitedSuggestions(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen || initialAssetId || !initialImageUrl || pinnedAssetId) return;

    const match = assets.find(
      (a) =>
        a.externalUrl === initialImageUrl ||
        a.url === initialImageUrl ||
        a.metadata?.sourceUrl === initialImageUrl,
    );

    if (match) {
      setPinnedAssetId(match.id);
      setSelectedAssetId(match.id);
    }
  }, [isOpen, initialAssetId, initialImageUrl, pinnedAssetId, assets]);

  const selectedAsset = selectedAssetId
    ? (singleAssets[selectedAssetId] ??
      assets.find((a) => a.id === selectedAssetId) ??
      (selectedFolderId
        ? assetsByFolder[selectedFolderId]?.find((a) => a.id === selectedAssetId)
        : undefined) ??
      Object.values(assetsByFolder)
        .flat()
        .find((a) => a.id === selectedAssetId))
    : undefined;

  const handleSelect = () => {
    const asset = selectedAsset;
    if (!asset?.url) return;

    onSelectImage({
      url: asset.url,
      assetId: asset.id,
      folderId: asset.folderId,
      filename: asset.filename ?? asset.metadata?.originalFilename,
      altText: asset.altText,
      description: asset.description,
      variants: asset.variants,
      contentType: asset.contentType,
    });
    onClose();
  };

  const hasSelectableAsset = Boolean(selectedAsset?.url);

  const handleSelectSuggestion = async (image?: UnsplashImage) => {
    const targetImage = image ?? selectedSuggestionImage;
    if (!targetImage || isSelectingSuggestionRef.current) return;

    isSelectingSuggestionRef.current = true;
    setIsSelectingSuggestion(true);
    try {
      const asset = await downloadUnsplashImage(targetImage.id);

      onSelectImage({
        url: asset.url,
        assetId: asset.id,
        folderId: asset.folderId,
        filename: asset.filename ?? asset.metadata?.originalFilename,
        altText: asset.altText,
        description: asset.description,
        contentType: asset.contentType,
        variants: asset.variants,
      });

      markStale();
      void loadAssets(fileTypes, true);
      onClose();
    } catch (error) {
      console.error('Failed to download Unsplash image', error);
      toast.error('Failed to add image. Please try again.');
    } finally {
      isSelectingSuggestionRef.current = false;
      setIsSelectingSuggestion(false);
    }
  };

  const handleAssetDoubleClick = (asset: Asset) => {
    if (asset.url) {
      onSelectImage({
        url: asset.url,
        assetId: asset.id,
        folderId: asset.folderId,
        filename: asset.filename ?? asset.metadata?.originalFilename,
        altText: asset.altText,
        description: asset.description,
        variants: asset.variants,
        contentType: asset.contentType,
      });
      onClose();
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && (isCreateFeatureBusy || isSelectingSuggestion)) return;
    if (!open) onClose();
  };

  const handleUploadComplete = () => {
    void loadAssets(fileTypes, true);
    setActiveTab('library');
  };

  const DEFAULT_MODAL_TITLE: Record<AssetFileTypes, string> = {
    images: 'Select Image',
    videos: 'Select Video',
    all: 'Select File',
  };
  const modalTitle = title ?? DEFAULT_MODAL_TITLE[fileTypes];
  const body = getDocument()?.body;

  return (
    <>
      {body && createImageEnabled
        ? createPortal(<div className="fixed inset-0 z-40 bg-black/50" aria-hidden="true" />, body)
        : null}
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange} modal={!createImageEnabled}>
        <DialogContent
          size="xl"
          dismissible={!isCreateFeatureBusy && !isSelectingSuggestion}
          showCloseButton={!isCreateFeatureBusy && !isSelectingSuggestion}
          className={cn(
            'h-[min(72vh,620px)]',
            isCreateFeatureBusy &&
              'min-[1200px]:!left-[calc((100vw_-_var(--assistant-panel-w))_/_2)] max-[1199px]:!z-[60]',
          )}
          style={
            isCreateFeatureBusy
              ? ({ '--assistant-panel-w': `${ASSISTANT_PANEL_WIDTH_PX}px` } as CSSProperties)
              : undefined
          }
          bodyClassName="flex flex-col overflow-hidden"
        >
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as typeof activeTab)}
            className="flex-1 min-h-0"
          >
            <TabsList variant="underline" fullWidth={false} className="max-w-full">
              {isImageMode ? (
                <TabsTrigger value="suggestions">
                  <Icon icon={faLightbulb} />
                  Suggestions
                </TabsTrigger>
              ) : null}
              {createImageEnabled ? (
                <TabsTrigger value="create">
                  <Icon icon={faSparkles} className="text-primary" />
                  Create an Image
                </TabsTrigger>
              ) : null}
              <TabsTrigger value="library">
                <Icon icon={faFolder} />
                Asset Library
              </TabsTrigger>
              <TabsTrigger value="upload">
                <Icon icon={faArrowDownToBracket} />
                Upload
              </TabsTrigger>
              {showYouTubeTab ? (
                <TabsTrigger value="youtube">
                  <Icon icon={faCode} />
                  YouTube
                </TabsTrigger>
              ) : null}
            </TabsList>

            {isImageMode ? (
              <TabsContent
                value="suggestions"
                forceMount
                hidden={activeTab !== 'suggestions'}
                className="min-h-0 flex flex-col"
              >
                {hasVisitedSuggestions ? (
                  <SuggestionsContent
                    selectedImageId={selectedSuggestionImage?.id ?? null}
                    isSelecting={isSelectingSuggestion}
                    onImageSelect={(image) =>
                      setSelectedSuggestionImage((prev) => (prev?.id === image.id ? null : image))
                    }
                    onImageDoubleClick={(image) => {
                      setSelectedSuggestionImage(image);
                      void handleSelectSuggestion(image);
                    }}
                    onSelectionClear={() => setSelectedSuggestionImage(null)}
                  />
                ) : null}
              </TabsContent>
            ) : null}

            {createImageEnabled ? (
              <ImageSelectorCreateImageFeature
                isOpen={isOpen}
                isActive={activeTab === 'create'}
                initialImageUrl={initialImageUrl}
                sectionId={sectionId}
                imageSuggestionTarget={imageSuggestionTarget}
                onRequestActivate={() => setActiveTab('create')}
                onSelectImage={onSelectImage}
                onClose={onClose}
                onBusyStateChange={setIsCreateFeatureBusy}
              />
            ) : null}

            <TabsContent value="library" className="min-h-0 flex flex-col">
              <LibraryFilesContent
                selectedAssetId={selectedAssetId}
                pinnedAssetId={pinnedAssetId}
                pinnedFolderId={selectedFolderId}
                onAssetClick={(asset) =>
                  setSelectedAssetId((prev) => (prev === asset.id ? null : asset.id))
                }
                onClearSelection={() => setSelectedAssetId(null)}
                onAssetDoubleClick={handleAssetDoubleClick}
                fileTypes={fileTypes}
              />
            </TabsContent>

            <TabsContent value="upload" forceMount hidden={activeTab !== 'upload'}>
              <div className="space-y-4">
                <UploadFilesPanel
                  fileTypes={fileTypes}
                  className="h-[330px]"
                  onUploadComplete={handleUploadComplete}
                />
                <UrlImportSection onUploadComplete={handleUploadComplete} />
              </div>
            </TabsContent>

            {showYouTubeTab ? (
              <TabsContent value="youtube" forceMount hidden={activeTab !== 'youtube'}>
                <YouTubeTabContent
                  initialUrl={initialYouTubeUrl ?? undefined}
                  onSelect={(url) => {
                    onYouTubeSelect?.(url);
                    onClose();
                  }}
                  onCancel={onClose}
                />
              </TabsContent>
            ) : null}
          </Tabs>

          {activeTab === 'library' || activeTab === 'suggestions' ? (
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isSelectingSuggestion}>
                Cancel
              </Button>
              <Button
                onClick={
                  activeTab === 'library'
                    ? handleSelect
                    : () => {
                        void handleSelectSuggestion();
                      }
                }
                disabled={activeTab === 'library' ? !hasSelectableAsset : !selectedSuggestionImage}
                isLoading={activeTab === 'suggestions' && isSelectingSuggestion}
                loadingText="Selecting..."
                loaderPlacement="start"
              >
                {selectLabel}
              </Button>
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
